import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { debounce } from 'lodash';

interface DataItem {
  周日期: string;
  产品编码: string;
  产品名称: string;
  销售渠道: string;
  销量: number;
}

interface PlatformWeeklySalesChartProps {
  data: DataItem[];
  loading?: boolean;
}

// 定义渐变色 - 100种有区分度的现代商务风颜色
const colors = [
  // 基础色彩系列 (10种)
  ['#1890ff', '#0050b3'],  // 蓝色系
  ['#52c41a', '#237804'],  // 绿色系
  ['#ffd666', '#ffc53d'],  // 浅金色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#722ed1', '#391085'],  // 紫色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#595959', '#262626'],  // 灰色系
  ['#a0d911', '#7cb305'],  // 黄绿系

  // 扩展色彩系列 - 明亮色调 (20种)
  ['#ff4d4f', '#cf1322'],  // 亮红色
  ['#ff7a45', '#d46b08'],  // 亮橙色
  ['#ffa940', '#fa8c16'],  // 亮黄色
  ['#fffb8f', '#faad14'],  // 亮浅黄
  ['#52c41a', '#237804'],  // 亮绿色
  ['#36cfc9', '#08979c'],  // 亮青色
  ['#1890ff', '#0958d9'],  // 亮蓝色
  ['#597ef7', '#2f54eb'],  // 亮靛蓝
  ['#9254de', '#722ed1'],  // 亮紫色
  ['#f759ab', '#eb2f96'],  // 亮粉色
  ['#ff13c2', '#a0d911'],  // 亮薄荷绿
  ['#ff9c6e', '#faad14'],  // 亮柠檬黄
  ['#b7282d', '#389e0d'],  // 亮深绿
  ['#4096ff', '#0958d9'],  // 亮天蓝
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙

  // 扩展色彩系列 - 中等色调 (25种)
  ['#73d13d', '#389e0d'],  // 草绿色
  ['#95de64', '#52c41a'],  // 薄荷绿
  ['#b7eb8f', '#73d13d'],  // 薄荷浅绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#13c2c2', '#08979c'],  // 青色系
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#69c0ff', '#40a9ff'],  // 浅天蓝
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫

  // 扩展色彩系列 - 柔和色调 (25种)
  ['#d3f261', '#a0d911'],  // 韠和黄绿
  ['#95de64', '#52c41a'],  // 柔和绿
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 柔和蓝
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#95de64', '#52c41a'],  // 草绿色
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙

  // 扩展色彩系列 - 特殊色调 (20种)
  ['#2f54eb', '#0958d9'],  // 靛蓝
  ['#722ed1', '#391085'],  // 紫色
  ['#52c41a', '#237804'],  // 绿色
  ['#fa8c16', '#d46b08'],  // 橙色
  ['#f5222d', '#cf1322'],  // 红色
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#1890ff', '#0050b3'],  // 蓝色系
  ['#52c41a', '#237804'],  // 绿色系
  ['#ffd666', '#ffc53d'],  // 浅金色系
  ['#f5222d', '#cf1322'],  // 红色系
  ['#722ed1', '#391085'],  // 紫色系
  ['#fa8c16', '#d46b08'],  // 橙色系
  ['#eb2f96', '#9e1068'],  // 粉色系
  ['#13c2c2', '#08979c'],  // 青色系
  ['#595959', '#262626'],  // 灰色系
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#95de64', '#52c41a'],  // 草绿色
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#f759ab', '#eb2f96'],  // 粉粉色
  ['#a0d911', '#7cb305'],  // 黄绿系
  ['#95de64', '#52c41a'],  // 草绿色
  ['#5cdbd3', '#13c2c2'],  // 柔和青绿
  ['#40a9ff', '#1890ff'],  // 天蓝色
  ['#adc6ff', '#597ef7'],  // 柔和靛蓝
  ['#d3adf7', '#9254de'],  // 柔和紫
  ['#ffadd2', '#f759ab'],  // 柔和粉
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ffccc7', '#ff4d4f'],  // 柔和红
  ['#ff9c6e', '#ff7a45'],  // 柔和珊瑚橙
  ['#8c8cff', '#5cadff'],  // 浅蓝色
  ['#b4d3ff', '#722ed1'],  // 粉紫色
  ['#d9d7ff', '#52c41a'],  // 草绿色
  ['#ffd591', '#fa8c16'],  // 柔和橙
  ['#ff7875', '#ff4d4f'],  // 柔和红
  ['#73c0de', '#13c2c2'],  // 青绿色
  ['#91d5ff', '#69c0ff'],  // 极浅天蓝
  ['#b37feb', '#9254de'],  // 薰荷紫
  ['#d3adf7', '#b37feb'],  // 浅薰衣草紫
  ['#ffadd2', '#f759ab'],  // 浅粉色
  ['#ffccc7', '#ff7875'],  // 浅珊瑚色
  ['#ff9c6e', '#ff7a45']   // 柔和珊瑚橙
];

export const PlatformWeeklySalesChart: React.FC<PlatformWeeklySalesChartProps> = ({ data, loading }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  // 处理数据
  const processData = (rawData: DataItem[]) => {
    if (!rawData || rawData.length === 0) return {
      weeks: [],
      platforms: [],
      series: [],
      productInfo: null
    };

    // 按周日期排序
    const sortedData = [...rawData].sort((a, b) => a.周日期.localeCompare(b.周日期));

    // 获取所有唯一的销售渠道，创建平台到颜色的映射
    const platforms = Array.from(new Set(sortedData.map(item => item.销售渠道)));
    const platformColorMap: { [key: string]: string } = {};
    platforms.forEach((platform, index) => {
      platformColorMap[platform] = colors[index % colors.length][0];
    });

    // 获取所有唯一的周日期
    const weeks = Array.from(new Set(sortedData.map(item => item.周日期)));

    // 为每个销售渠道创建数据系列
    const series = platforms.map(platform => {
      const data = weeks.map(week => {
        const matchingItem = sortedData.find(item =>
          item.周日期 === week && item.销售渠道 === platform
        );
        return matchingItem ? matchingItem.销量 : 0;
      });

      return {
        name: platform,
        type: 'line',
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8
        },
        emphasis: {
          focus: 'series'
        },
        data: data
      };
    });

    return {
      weeks,
      platforms,
      series,
      platformColorMap,
      productInfo: sortedData[0] || null
    };
  };

  // 初始化图表
  const initChart = () => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const { weeks, platforms, series, platformColorMap, productInfo } = processData(data);

    const option: EChartsOption = {
      title: {
        text: '',
        left: 'center',
        top: 0,
        textStyle: {
          fontSize: 16,
          fontWeight: 'bold'
        },
        subtextStyle: {
          fontSize: 12,
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: (params: any[]) => {
          if (!params[0]) return '';

          let result = `
            <div class="font-bold mb-2">${params[0].axisValue}</div>
          `;

          // 显示产品信息
          if (productInfo) {
            result += `<div class="mb-2">`;
            result += `<div>产品编码：${productInfo.产品编码}</div>`;
            result += `<div>产品名称：${productInfo.产品名称}</div>`;
            result += `</div>`;
          }

          // 按销量从大到小排序
          params.sort((a, b) => b.value - a.value);

          params.forEach(param => {
            const color = platformColorMap[param.seriesName] || param.color;
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:5px;width:12px;height:8px;background-color:${color};"></span>`;
            result += `${marker}${param.seriesName}: ${param.value.toLocaleString()} 件<br/>`;
          });

          return result;
        }
      },
      legend: {
        data: platforms.map((platform, index) => ({
          name: platform,
          itemStyle: {
            color: colors[index % colors.length][0]
          }
        })),
        top: 30,
        type: 'scroll',
        textStyle: {
          fontSize: 12
        },
        icon: 'circle',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 15
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
        top: 100
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: weeks,
        axisLabel: {
          rotate: 45,
          formatter: (value: string) => {
            // 格式化日期显示，如 "2025-08-25" 显示为 "8月25日"
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return value; // 如果日期无效，返回原值
            }
            return `${date.getMonth() + 1}月${date.getDate()}日`;
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '销量',
        axisLabel: {
          formatter: (value: number) => value.toLocaleString()
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
          },
        }
      },
      series: series.map((item, index) => ({
        ...item,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: colors[index % colors.length][0]
            },
            {
              offset: 1,
              color: colors[index % colors.length][1]
            }
          ])
        }
      }))
    };

    chartInstance.current.setOption(option);
  };

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 更新图表数据
  useEffect(() => {
    if (data && data.length > 0) {
      initChart();
    }
  }, [data]);

  // 销毁图表实例
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full h-[500px] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div ref={chartRef} className="w-full h-[500px]" />
  );
};

export default PlatformWeeklySalesChart;